import { useCallback } from "react";
import { en, type Dictionary, type TranslationKey } from "@/src/i18n/en";
import { he } from "@/src/i18n/he";
import { useMapStore } from "@/src/store/useMapStore";
import type { Language } from "@/src/i18n/language";

export type { TranslationKey, Dictionary };
export { en };

export {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  directionFor,
  isLanguage,
  isRtl,
  type Language,
} from "@/src/i18n/language";

/** English is complete by construction; other dictionaries may be partial. */
const dictionaries: Record<Language, Dictionary | Partial<Dictionary>> = { en, he };

export type TranslationParams = Record<string, string | number> & { count?: number };

const substitute = (template: string, params?: TranslationParams): string => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
};

/**
 * Resolves a key for a language, falling back to English per key rather than
 * per dictionary — a partly translated `he.ts` still renders a complete UI.
 * An unknown key returns the key itself, which is loud in the UI and harmless.
 */
export const translate = (
  language: Language,
  key: TranslationKey,
  params?: TranslationParams,
): string => {
  const count = params?.count;
  if (typeof count === "number") {
    const pluralKey = `${key}_${count === 1 ? "one" : "other"}` as TranslationKey;
    if (pluralKey in en) return resolve(language, pluralKey, params);
  }
  return resolve(language, key, params);
};

const resolve = (
  language: Language,
  key: TranslationKey,
  params?: TranslationParams,
): string => {
  const localised = dictionaries[language]?.[key];
  const template = localised ?? en[key];
  if (template === undefined) return key;
  return substitute(template, params);
};

/**
 * Non-reactive lookup for modules outside React (store defaults, exporters,
 * the renderer). Reads the current language at call time.
 */
export const t = (key: TranslationKey, params?: TranslationParams): string =>
  translate(useMapStore.getState().language, key, params);

/** Reactive lookup for components. Re-renders when the language changes. */
export const useT = () => {
  const language = useMapStore((state) => state.language);
  return useCallback(
    (key: TranslationKey, params?: TranslationParams) => translate(language, key, params),
    [language],
  );
};

export const useLanguage = () => useMapStore((state) => state.language);
